<script lang="ts">
    import Btn from "../components/Btn.svelte";
    import Container from "../components/Container.svelte";
    import Divider from "../components/Divider.svelte";
    import Dropdown from "../components/Dropdown.svelte";
    import Gap from "../components/Gap.svelte";
    import Headline from "../components/Headline.svelte";
    import Hero from "../components/Hero.svelte";
    import Image from "../components/Image.svelte";
    import LightTheme from "../components/LightTheme.svelte";
    import PageTransition from "../components/PageTransition.svelte";
    import Row from "../components/Row.svelte";
    import Subline from "../components/Subline.svelte";
    import { fade } from "svelte/transition";
    import IconImg from "../components/IconImg.svelte";
    import { onMount } from "svelte";
    import { t } from "../helpers/translation";

    let sending_feedback_text = "";
    let sending_failed = false;
    let form_is_sending = false;

    let formdata = {
        name: "",
        email: "",
        tel: "",
        subject: "",
        message: "",
    };

    let form: HTMLFormElement = null;
    let recaptchaRef: HTMLDivElement = null;

    let formfields = {
        subject: null,
        message: null,
    };

    onMount(() => {
        window["grecaptcha"]?.render(recaptchaRef, {
            sitekey: "6LeTTxwUAAAAAGJQqRfoqmlEsuZCPj8uJDDsLewa",
        });
    });

    const sendToApi = async (): Promise<boolean> => {
        const payload = {
            ...formdata,
            gRecaptchaResponse: window["grecaptcha"]?.getResponse(),
        };
        return await fetch("/api/EmailHinweisgeberschutzSend", {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        })
            .then((res) => {
                if (res.ok) return res.json();
                else throw new Error();
            })
            .catch((err) => {
                console.error(err);
                return false;
            });
    };

    const sendForm = async () => {
        for (const name in formfields) {
            const field = formfields[name];
            if (field.validity.valid == false) {
                field.reportValidity();
                return;
            }
        }
        form_is_sending = true;
        const success = await sendToApi();
        form_is_sending = false;
        sending_feedback_text = success
            ? $t("page.hinweisgeberschutz.form.success")
            : $t("page.hinweisgeberschutz.form.error");
        sending_failed = !success;

        if (!sending_failed) form.reset();
    };
</script>

<LightTheme />

<Hero headline={$t("page.hinweisgeberschutz.hero.headline")} small />

<Container>
    <center>
        <Headline>
            {@html $t("page.hinweisgeberschutz.headline")}
        </Headline>
        <Subline>
            {@html $t("page.hinweisgeberschutz.subline")}
        </Subline>
        <!-- <Gap height="1rem" /> -->
    </center>
</Container>

<Container max="700px">
    <Gap height="2rem" />

    <p>
        {@html $t("page.hinweisgeberschutz.section.1.p.1")}
    </p>
    <p>
        {@html $t("page.hinweisgeberschutz.section.1.p.2")}
    </p>
    <p>
        {@html $t("page.hinweisgeberschutz.section.1.p.3")}
    </p>

    <Gap height="4rem" />

    <form action="post" bind:this={form}>
        <label for="name">
            <span>
                {@html $t("page.hinweisgeberschutz.form.field.name")}
            </span>
            <input
                type="text"
                name="name"
                id="name"
                bind:value={formdata.name}
                placeholder="Optional"
            />
        </label>
        <label for="email">
            <span>
                {@html $t("page.hinweisgeberschutz.form.field.email")}
            </span>
            <input
                type="email"
                name="email"
                id="email"
                bind:value={formdata.email}
                placeholder="Optional"
            />
        </label>
        <label for="tel">
            <span>
                {@html $t("page.hinweisgeberschutz.form.field.phone")}
            </span>
            <input
                type="tel"
                name="tel"
                id="tel"
                bind:value={formdata.tel}
                placeholder="Optional"
            />
        </label>
        <label for="subject">
            <span>
                {@html $t("page.hinweisgeberschutz.form.field.subject")}
                *
            </span>
            <input
                type="text"
                name="subject"
                id="subject"
                bind:value={formdata.subject}
                bind:this={formfields.subject}
                required
            />
        </label>
        <label for="message">
            <span>
                {@html $t("page.hinweisgeberschutz.form.field.message")}
                *
            </span>
            <textarea
                rows="7"
                name="message"
                id="message"
                bind:value={formdata.message}
                bind:this={formfields.message}
                required
                placeholder="..."
            />
        </label>

        <Divider gap="2rem" />

        <div
            bind:this={recaptchaRef}
            class="g-recaptcha"
            data-sitekey="6LeTTxwUAAAAAGJQqRfoqmlEsuZCPj8uJDDsLewa"
        ></div>

        <Divider gap="2rem" />

        <div style="min-height: 8rem; text-align: center">
            <Btn
                blinking={form_is_sending}
                on:click={sendForm}
                small={form_is_sending}
                >{form_is_sending
                    ? $t("page.hinweisgeberschutz.form.btn.sending")
                    : $t("page.hinweisgeberschutz.form.btn.send")}</Btn
            >
            {#if sending_feedback_text}
                <div
                    class="sending-feedback"
                    class:sending_failed
                    transition:fade
                >
                    {sending_feedback_text}
                </div>
            {/if}
        </div>
    </form>

    <Gap height="4rem" />

    <p>
        {@html $t("page.hinweisgeberschutz.section.2.p.1")}
    </p>
    <p>
        {@html $t("page.hinweisgeberschutz.section.2.p.2")}
    </p>
    <p>
        {@html $t("page.hinweisgeberschutz.section.2.p.3")}
    </p>
    <p>
        {@html $t("page.hinweisgeberschutz.section.2.p.4")}
    </p>
</Container>

<Gap height="5vw" />

<style lang="scss">
    @import "../vars";

    .contact-infos {
        display: grid;
        grid-template-columns: 1fr 2fr;
        gap: 1rem;

        @media (max-width: 600px) {
            grid-template-columns: 1fr;
        }

        --color: var(--green);

        .partner {
            border-left: 1rem solid var(--color);
            padding-left: 1rem;
            h4 {
                margin: 0em !important;
            }
            p {
                margin-top: 0.5em;
                font-size: 1rem;
                line-height: 1.3em;
            }
            a {
                color: var(--grayDark2);
            }
        }
    }

    form {
        display: grid;
        place-items: center;
        label {
            font-size: 1.2rem;
            width: 100%;
            display: grid;
            grid-template-columns: 0.8fr 2fr;
            text-align: right;
            padding-bottom: 0.5em;
            @media (max-width: 650px) {
                grid-template-columns: 1fr;
                text-align: left;
            }
            input,
            span,
            select,
            textarea {
                padding: 0.5em 1em;
                font-size: 1em;
            }
            input,
            select,
            textarea {
                background-color: white;
                border: 1px solid $gray;
                outline: unset;
                border-radius: 0.5em;
            }
            input::placeholder {
                color: $grayLight;
            }
            textarea {
                resize: vertical;
            }
        }
        .sending-feedback {
            text-align: center;
            padding: 1em;
            width: auto;
            margin: 1em auto;
            color: $green;
            font-weight: 700;
            &.sending_failed {
                color: red;
            }
        }
    }
</style>
